import { useContext, useCallback } from 'react'
import cn from 'classnames'
import { Link } from 'react-router-dom'
import { format } from 'date-fns'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faBolt,
  faFireAlt,
  faWrench,
  faTag,
  faUserClock,
  faBoxFull,
} from '@fortawesome/pro-regular-svg-icons'
import {
  faInfoCircle,
  faCalendarAlt,
  faAngleRight,
  faUserCircle,
} from '@fortawesome/pro-light-svg-icons'

import { toCapitalCase } from '../../../utils/strings'
import Label from '../../Label'
import Badge from '../../Badge'
import AuthContext from '../../../context/AuthContext'
import BrandingContext from '../../../context/BrandingContext'
import {
  getStatus,
  getUploadLabelColor,
  getTune,
  getTuneIcon,
  getVehicleTypeIcon,
} from '../../../utils/uploads'

const UploadListItem = (props) => {
  const { upload, page, pathname, isAdmin, filters, dealerNumberPrefix } = props
  const [auth] = useContext(AuthContext)
  const [branding] = useContext(BrandingContext)
  const { primaryColorPalette } = branding

  const isWhiteLabelMaster =
    !branding.isTgt && auth.membership.dealer.id === branding.masterDealerId

  const leftCn = cn('min-w-65', {
    'flex-1': !isAdmin && !isWhiteLabelMaster,
    'flex-[3]': isAdmin || isWhiteLabelMaster,
  })

  const middleCn = cn('min-w-65', {
    'flex-[2]': !isAdmin && !isWhiteLabelMaster,
    'flex-[3]': isAdmin || isWhiteLabelMaster,
  })

  const rightCn = cn('min-w-65', {
    'flex-[2]': isAdmin || isWhiteLabelMaster,
  })

  const renderVehicleReference = () => {
    if (upload.vrm) {
      return upload.vrm
    }

    if (upload.reference) {
      return upload.reference
    }

    return null
  }

  const renderAssignmentTags = useCallback(() => {
    if (
      !auth.membership.accountType === 'ADMIN' ||
      !upload.assignedTo?.length
    ) {
      return
    }

    const assignees = [...upload.assignedTo]

    // Make sure the current user is always on top of list
    const index = assignees.findIndex(
      (assignee) => assignee.id === auth.membership.id,
    )

    if (index >= 0) {
      assignees.splice(0, 0, assignees.splice(index, 1)[0])
    }

    return assignees.map((assignee) =>
      assignee.id === auth.membership.id ? (
        <Badge icon={faTag} color="orange" key={assignee.id}>
          You
        </Badge>
      ) : (
        <Badge icon={faTag} color="blue" key={assignee.id}>
          {assignee.user.firstName} {assignee.user.lastName}
        </Badge>
      ),
    )
  }, [auth.membership, upload])

  const carMake = upload.vehicle?.make || upload.manualVehicle?.make
  const carModel = upload.vehicle?.model || upload.manualVehicle?.model
  const carYear = upload.vehicle?.startYear || upload.manualVehicle?.year
  const carFuel = upload.vehicle?.fuel || upload.manualVehicle?.fuel
  const carEngineSize =
    upload.vehicle?.engineSize || upload.manualVehicle?.engineSize
  const vehicleReference = renderVehicleReference()
  const analysisTypeLabel = upload.analysisType === 'AUTO' ? 'Auto' : 'Manual'
  const analysisTypeColor =
    upload.analysisType === 'AUTO' ? 'gray-dark' : 'gray'
  const analysisTypeIcon = upload.analysisType === 'AUTO' ? faBolt : faUserClock

  return (
    <Link
      className="border-b-4 border-gray-100 relative hover:bg-gray-50"
      to={{
        pathname: `/uploads/view/${upload.id}`,
        state: {
          upload,
          listState: {
            page,
            pathname,
            filters,
          },
        },
      }}
    >
      <div className="flex space-x-3 pr-3 pl-7 pt-4 pb-3 relative">
        <div className={leftCn}>
          <p
            className={cn(
              `text-${primaryColorPalette}-600 text-sm mb-1.5 h-5 uppercase leading-5`,
              {
                'font-medium': !upload.isUnreplied,
                'font-semibold': upload.isUnreplied,
              },
            )}
          >
            {upload.vehicleType === 'CAR' && (
              <>
                {carMake} {carModel} {carYear}
              </>
            )}
            {upload.vehicleType !== 'CAR' && (
              <>
                {upload.manualVehicle.make} {upload.manualVehicle.model}{' '}
                {upload.manualVehicle.year}
              </>
            )}
          </p>
          <p className="text-gray-600 text-xs">
            <FontAwesomeIcon
              className="text-gray-500 mr-2"
              icon={faInfoCircle}
              size="lg"
            />
            {vehicleReference ? `${vehicleReference} • ` : ''}
            {upload.vehicleType === 'CAR' && (
              <>
                {toCapitalCase(carFuel || '')} • {carEngineSize}cc
              </>
            )}
            {upload.vehicleType !== 'CAR' && (
              <>
                {upload.manualVehicle.hpModel} •{' '}
                {upload.manualVehicle.engineSize}
              </>
            )}
          </p>
        </div>
        <div className={middleCn}>
          <p
            className={cn(
              'text-gray-900 mb-1.5 h-5 text-sm leading-5 whitespace-nowrap mr-6',
              {
                'font-medium': upload.isUnreplied,
              },
            )}
          >
            Upload number {upload.uploadNumber}
            <Label color={getUploadLabelColor(upload.status)} className="ml-2">
              {getStatus(upload.status)}
            </Label>
          </p>
          <p className="text-gray-600 text-xs">
            <FontAwesomeIcon
              className="text-gray-500 mr-2"
              icon={faCalendarAlt}
              size="lg"
            />
            Uploaded{' '}
            {format(new Date(upload.createdAt), "dd MMMM yyy 'at' hh:mma")}
          </p>
        </div>
        {(isAdmin || isWhiteLabelMaster) && (
          <div className={rightCn}>
            <p
              className={cn('text-gray-900 mb-1.5 text-sm mr-4 leading-5', {
                'font-medium': upload.isUnreplied,
              })}
            >
              {upload.createdBy.dealer.name}
            </p>
            <p className="text-gray-600 text-xs">
              <FontAwesomeIcon
                className="text-gray-500 mr-2"
                icon={faUserCircle}
                size="lg"
              />
              {dealerNumberPrefix} number {dealerNumberPrefix}-
              {upload.createdBy.dealer.tgtNumber.slice(4)}
            </p>
          </div>
        )}
        <div>
          <FontAwesomeIcon
            className="absolute right-5 mt-1 top-1/2 -translate-y-1/2 text-gray-400"
            size="lg"
            icon={faAngleRight}
          />
        </div>
      </div>
      <div className="border-dashed border-t border-gray-200 py-2 ml-7 mr-5 flex items-center justify-start space-x-2">
        {renderAssignmentTags()}
        {upload.priority && (
          <Badge icon={faFireAlt} color="pink">
            Priority file
          </Badge>
        )}
        <Badge icon={analysisTypeIcon} color={analysisTypeColor}>
          {analysisTypeLabel}
        </Badge>
        <Badge icon={getVehicleTypeIcon(upload.vehicleType)} color="gray">
          {toCapitalCase(upload.vehicleType)}
        </Badge>
        <Badge icon={getTuneIcon(upload.tune.tuneRequired)} color="gray">
          {getTune(upload.tune.tuneRequired)}
        </Badge>
        <Badge
          icon={upload.uploadType === 'FULL' ? faBoxFull : faWrench}
          color="gray"
        >
          {upload.uploadType === 'FULL' ? 'Full' : 'Adjustment'} remap
        </Badge>
      </div>
      {upload.isUnreplied && (
        <span
          className={`absolute my-2 left-0 top-0 bottom-0 w-1 bg-${primaryColorPalette}-600 rounded-tr-md rounded-br-md`}
        />
      )}
    </Link>
  )
}

export default UploadListItem
